import React, { useCallback, useState } from 'react';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Check, WarningAmber } from '@mui/icons-material';
import { TextField } from 'c-components';
import type { CampaignInsertionOrder, InsertionOrderKey } from 'c-sdk';
import { useCommonTranslation } from 'c-translation';
import { parseCpmInput } from './cpmInput';
import { formatImpressionsTarget, parseImpressionsTargetInput } from './impressionsTargetInput';

/**
 * The insertion order details edited in place on the DSP schedule page: CPM (UL-7912) and the
 * impressions target (UL-8133). Both are saved through the detail endpoint, which writes only the
 * key sent, so neither cell can change the other.
 */

/**
 * CPM, edited in place. Saved on the tick or on Enter; keyed on the catalogue's own key, so it
 * survives the rule that attached the insertion order being edited or deleted.
 */
export const CpmCell: React.FC<{
    row: CampaignInsertionOrder;
    onSave: (key: InsertionOrderKey, cpm: number | null) => Promise<boolean>;
}> = ({ row, onSave }) => {
    const t = useCommonTranslation();
    const [value, setValue] = useState(row.cpm ?? '');
    const { parsed, invalid, unchanged } = parseCpmInput(value, row.cpm);

    const save = useCallback(async () => {
        if (invalid || unchanged) return;
        await onSave(
            { customer: row.customer, insertion_order_id: row.insertion_order_id },
            parsed,
        );
    }, [invalid, unchanged, onSave, row.customer, row.insertion_order_id, parsed]);

    return (
        <Stack direction="row" alignItems="center" onClick={e => e.stopPropagation()}>
            <TextField
                size="small"
                value={value}
                error={invalid}
                onChange={e => setValue(e.target.value)}
                onKeyDown={e => {
                    e.stopPropagation();
                    if (e.key === 'Enter') save();
                }}
                inputProps={{ inputMode: 'decimal', 'aria-label': 'CPM' }}
                sx={{ width: 90 }}
            />
            {!unchanged && (
                <Tooltip
                    title={t(
                        invalid
                            ? 'Modules.Main.Campaigns.CampaignSchedule.dsp.cpm.invalid'
                            : 'Modules.Main.Campaigns.CampaignSchedule.dsp.cpm.save',
                    )}
                >
                    <span>
                        <IconButton size="small" disabled={invalid} onClick={save}>
                            <Check fontSize="inherit" />
                        </IconButton>
                    </span>
                </Tooltip>
            )}
        </Stack>
    );
};

/**
 * UL-8133 -- the impressions target, edited in place like CPM. Saved on the tick or on Enter; an
 * invalid entry shows the reason on the tick's tooltip and in the field's helper text, and is
 * never sent. Blank clears the target.
 */
export const ImpressionsTargetCell: React.FC<{
    row: Pick<CampaignInsertionOrder, 'customer' | 'insertion_order_id' | 'impressions_target'>;
    onSave: (key: InsertionOrderKey, target: number | null) => Promise<boolean>;
    /** For rows whose key is not the catalogue's own (planned lines). */
    label?: string;
}> = ({ row, onSave, label }) => {
    const t = useCommonTranslation();
    const [value, setValue] = useState(formatImpressionsTarget(row.impressions_target));
    const { parsed, invalid, unchanged } = parseImpressionsTargetInput(
        value,
        row.impressions_target,
    );
    const ariaLabel =
        label ?? t('Modules.Main.Campaigns.CampaignSchedule.dsp.columns.impressionsTarget');

    const save = useCallback(async () => {
        if (invalid || unchanged) return;
        await onSave(
            { customer: row.customer, insertion_order_id: row.insertion_order_id },
            parsed,
        );
    }, [invalid, unchanged, onSave, row.customer, row.insertion_order_id, parsed]);

    return (
        <Stack direction="row" alignItems="center" onClick={e => e.stopPropagation()}>
            <TextField
                size="small"
                value={value}
                error={invalid}
                onChange={e => setValue(e.target.value)}
                onKeyDown={e => {
                    e.stopPropagation();
                    if (e.key === 'Enter') save();
                }}
                inputProps={{ inputMode: 'decimal', 'aria-label': ariaLabel }}
                sx={{ width: 130 }}
            />
            {!unchanged && (
                <Tooltip
                    title={
                        invalid
                            ? t(
                                  'Modules.Main.Campaigns.CampaignSchedule.dsp.impressionsTarget.invalid',
                              )
                            : t('CommonWords.Save')
                    }
                >
                    <span>
                        <IconButton
                            size="small"
                            disabled={invalid}
                            onClick={save}
                            aria-label={
                                invalid
                                    ? t(
                                          'Modules.Main.Campaigns.CampaignSchedule.dsp.impressionsTarget.invalid',
                                      )
                                    : t('CommonWords.Save')
                            }
                        >
                            <Check fontSize="inherit" />
                        </IconButton>
                    </span>
                </Tooltip>
            )}
        </Stack>
    );
};

/** A read-only target: thousands separators, decimals only when present, a dash for none. */
export const ImpressionsTargetText: React.FC<{ value: number | null | undefined }> = ({
    value,
}) => <Typography variant="body2">{formatImpressionsTarget(value) || '—'}</Typography>;

/**
 * UL-7967 -- marks an insertion order with no impressions target. Pacing understates while targets
 * are missing (Expected Impressions is the sum of the targets), so a low figure needs a visible
 * reason. Shown for a blank target only: 0 was typed and is not missing.
 */
export const MissingTargetMarker: React.FC = () => {
    const t = useCommonTranslation();
    const label = t('Modules.Main.Campaigns.CampaignSchedule.dsp.missingTarget');
    return (
        <Tooltip title={label}>
            <WarningAmber color="warning" fontSize="small" titleAccess={label} />
        </Tooltip>
    );
};

/**
 * UL-8136 -- a planned line's label, edited in place. Saved on the tick or on Enter; a blank label
 * is refused (a planned line always needs one).
 */
export const PlaceholderLabelCell: React.FC<{
    label: string;
    onSave: (label: string) => Promise<boolean>;
}> = ({ label, onSave }) => {
    const t = useCommonTranslation();
    const [value, setValue] = useState(label);
    const trimmed = value.trim();
    const invalid = trimmed === '';
    const unchanged = trimmed === label;

    const save = useCallback(async () => {
        if (invalid || unchanged) return;
        await onSave(trimmed);
    }, [invalid, onSave, trimmed, unchanged]);

    return (
        <Stack direction="row" alignItems="center" onClick={e => e.stopPropagation()}>
            <TextField
                size="small"
                value={value}
                error={invalid}
                onChange={e => setValue(e.target.value)}
                onKeyDown={e => {
                    e.stopPropagation();
                    if (e.key === 'Enter') save();
                }}
                inputProps={{
                    'aria-label': t(
                        'Modules.Main.Campaigns.CampaignSchedule.dsp.placeholders.label',
                    ),
                }}
                sx={{ width: 200 }}
            />
            {!unchanged && (
                <IconButton
                    size="small"
                    disabled={invalid}
                    onClick={save}
                    aria-label={t('CommonWords.Save')}
                >
                    <Check fontSize="inherit" />
                </IconButton>
            )}
        </Stack>
    );
};
